import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useSavePanDetails,
  useSaveNoPanDetails,
  useFetchPanDetails,
} from "../api";
import {
  PAN_REGEX,
  NO_PAN_REASONS,
  OTHER_NO_PAN_REASON,
} from "../utils/constants";

/**
 *
 * @param {Object} params
 * @param {string} params.userId
 * @param {Object} [params.currentDetails] - Prefill data (havingPan, nameAsPerPan, dobAsPerPan).
 * @param {boolean} params.isOpen - The form resets whenever the owning modal opens.
 * @returns {{ state, loading, derived, handlers, mutators }}
 */
export const usePanDetailsForm = ({ userId, currentDetails = {}, isOpen }) => {
  const fileInputRef = useRef(null);
  const form60InputRef = useRef(null);
  const reasonTriggerRef = useRef(null);

  const initialValuesRef = useRef({});

  // The fields a mentor actually fills in, grouped into one object instead of one useState
  // per field — the meta/UI state below (files, dropdown, errors) stays separate since it
  // isn't part of "pan details" and changes on a different cadence.
  const [panDetails, setPanDetails] = useState({
    hasPan: "yes",
    fullName: "",
    panNumber: "",
    dob: "",
    reasonForNoPan: "",
    otherReasonText: "",
  });
  const { hasPan, fullName, panNumber, dob, reasonForNoPan, otherReasonText } =
    panDetails;

  const [isAadharLinked, setIsAadharLinked] = useState("yes");
  const [panFile, setPanFile] = useState(null);
  const [existingPanDocumentUrl, setExistingPanDocumentUrl] = useState(null);
  const [existingPanDocumentName, setExistingPanDocumentName] = useState(null);
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  const [form60File, setForm60File] = useState(null);
  const [existingForm60DocumentUrl, setExistingForm60DocumentUrl] =
    useState(null);
  const [existingForm60DocumentName, setExistingForm60DocumentName] =
    useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [verificationStatus, setVerificationStatus] = useState(null);

  const isEditingExisting = Object.keys(currentDetails).length > 0;

  const { mutate: savePanDetails, loading: savingPan } =
    useSavePanDetails(userId);
  const { mutate: saveNoPanDetails, loading: savingNoPan } =
    useSaveNoPanDetails(userId);
  const isSaving = savingPan || savingNoPan;

  const { mutate: fetchFreshPanDetails } = useFetchPanDetails(userId, {
    manual: true,
  });

  const viewDocument = async (documentType) => {
    const data = await fetchFreshPanDetails();
    const freshUrl = data?.documents?.find(
      (doc) => doc.documentType === documentType,
    )?.url;
    if (freshUrl) window.open(freshUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (isOpen) {
      const previousReason = currentDetails.reason || "";
      const isKnownReason = NO_PAN_REASONS.some(
        (r) => r.value === previousReason,
      );
      const resolvedReasonForNoPan =
        previousReason && !isKnownReason ? OTHER_NO_PAN_REASON : previousReason;
      const resolvedOtherReasonText =
        previousReason && !isKnownReason ? previousReason : "";

      const initial = {
        havingPan: currentDetails.havingPan === "no" ? "no" : "yes",
        fullName: currentDetails.nameAsPerPan || "",
        panNumber: currentDetails.panNumber || "",
        dob: currentDetails.dobAsPerPan || "",
        reasonForNoPan: resolvedReasonForNoPan,
        otherReasonText: resolvedOtherReasonText,
      };
      initialValuesRef.current = initial;

      setPanDetails({
        hasPan: initial.havingPan,
        fullName: initial.fullName,
        panNumber: initial.panNumber,
        dob: initial.dob,
        reasonForNoPan: initial.reasonForNoPan,
        otherReasonText: initial.otherReasonText,
      });
      setIsAadharLinked("yes");
      setPanFile(null);
      setExistingPanDocumentUrl(currentDetails.panDocumentUrl || null);
      setExistingPanDocumentName(currentDetails.panDocumentName || null);
      setIsReasonDropdownOpen(false);
      setForm60File(null);
      setExistingForm60DocumentUrl(currentDetails.form60DocumentUrl || null);
      setExistingForm60DocumentName(currentDetails.form60DocumentName || null);
      setErrors({});
      setApiError("");
      setVerificationStatus(null);
    }
    // Deliberately NOT depending on currentDetails: it's a fresh object literal on every one of
    // the owning modal's re-renders, so keying this on it would re-run the reset — silently
    // wiping whatever the mentor had typed — any time something elsewhere caused a re-render
    // while this modal was open. Only isOpen actually flipping false->true means "new edit session."
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const clearError = (field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setPanDetailsField = (field, value) =>
    setPanDetails((prev) => ({ ...prev, [field]: value }));

  const setHasPan = (value) => {
    setPanDetailsField("hasPan", value);
    clearError("hasPan");
  };

  const setFullName = (value) => {
    setPanDetailsField("fullName", value);
    clearError("fullName");
  };

  const setPanNumber = (value) => {
    setPanDetailsField("panNumber", value.toUpperCase());
    clearError("panNumber");
  };

  const setDob = (value) => {
    setPanDetailsField("dob", value);
    clearError("dob");
  };

  const setReasonForNoPan = (value) => {
    setPanDetails((prev) => ({
      ...prev,
      reasonForNoPan: value,
      otherReasonText: value !== OTHER_NO_PAN_REASON ? "" : prev.otherReasonText,
    }));
    clearError("reasonForNoPan");
    if (value !== OTHER_NO_PAN_REASON) clearError("otherReasonText");
    setIsReasonDropdownOpen(false);
  };

  const setOtherReasonText = (value) => {
    setPanDetailsField("otherReasonText", value);
    clearError("otherReasonText");
  };

  const toggleReasonDropdown = () => setIsReasonDropdownOpen((prev) => !prev);
  const closeReasonDropdown = () => setIsReasonDropdownOpen(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPanFile(file);
    clearError("panFile");
    e.target.value = null;
  };

  const handleForm60Select = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm60File(file);
    clearError("form60File");
    e.target.value = null;
  };

  // The hasPan !== ... check matters on its own: switching branches without touching any field
  // in the newly-selected one (e.g. toggling to "no" and stopping) must still count as dirty, or
  // the toggle silently gets dropped on a combined pan+bank save (see touchPan in
  // EditPanAndBankDetailsModal.jsx — it only validates/submits sides isDirty flags as changed).
  const isDirty =
    hasPan !== initialValuesRef.current.havingPan ||
    (hasPan === "no"
      ? reasonForNoPan !== initialValuesRef.current.reasonForNoPan ||
        otherReasonText !== initialValuesRef.current.otherReasonText ||
        !!form60File
      : fullName !== initialValuesRef.current.fullName ||
        panNumber !== initialValuesRef.current.panNumber ||
        dob !== initialValuesRef.current.dob ||
        !!panFile);

  const validate = () => {
    const newErrors = {};
    if (!hasPan) {
      newErrors.hasPan = "Please select an option";
      setErrors(newErrors);
      return newErrors;
    }

    if (hasPan === "no") {
      if (!reasonForNoPan) {
        newErrors.reasonForNoPan = "Please select a reason";
      } else if (
        reasonForNoPan === OTHER_NO_PAN_REASON &&
        !otherReasonText.trim()
      ) {
        newErrors.otherReasonText = "Please tell us your reason";
      }
      if (!form60File) newErrors.form60File = "Please upload signed Form 60";
      setErrors(newErrors);
      return newErrors;
    }

    if (!fullName.trim()) newErrors.fullName = "Full name is required";
    if (!panNumber.trim()) {
      newErrors.panNumber = "PAN number is required";
    } else if (!PAN_REGEX.test(panNumber.trim())) {
      newErrors.panNumber = "Enter a valid PAN (e.g. ABCDE1234F)";
    }
    if (!dob) newErrors.dob = "Date of birth is required";
    // Skipped on edit — the backend only re-uploads when a file is actually sent
    // (panDetails.js's handlePanSubmit: `if (req.file) {...}`), so a mentor editing an
    // existing record doesn't have to re-select it just to change another field.
    if (!isEditingExisting && !panFile)
      newErrors.panFile = "Please upload your PAN document";
    setErrors(newErrors);
    return newErrors;
  };

  // Assumes validate() already passed. Resolves { requestOk, verified, data } —
  // requestOk: the API call succeeded; verified: fully saved & verified (safe to close + toast).
  const submit = async () => {
    setApiError("");
    setVerificationStatus(null);

    if (hasPan === "no") {
      const formData = new FormData();
      const reasonToSubmit =
        reasonForNoPan === OTHER_NO_PAN_REASON
          ? otherReasonText.trim()
          : reasonForNoPan;
      formData.append("reason", reasonToSubmit);
      formData.append("form60File", form60File);

      let requestOk = false;
      let data;
      await saveNoPanDetails({
        data: formData,
        config: { headers: { "Content-Type": "multipart/form-data" } },
        onSuccess: (resData) => {
          requestOk = true;
          data = resData;
        },
        onError: () => {
          setApiError(
            "Something went wrong while saving your details. Please try again.",
          );
        },
      });
      return { requestOk, verified: requestOk, data };
    }

    const formData = new FormData();
    formData.append("panNumber", panNumber.trim().toUpperCase());
    formData.append("nameAsPerPan", fullName.trim());
    formData.append("dobAsPerPan", dob);
    if (panFile) formData.append("panFile", panFile);

    let requestOk = false;
    let verified = false;
    let data;
    await savePanDetails({
      method: isEditingExisting ? "put" : "post",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
      onSuccess: (resData) => {
        requestOk = true;
        data = resData;
        setVerificationStatus(resData?.verificationStatus || null);
        verified = resData?.verificationStatus === "verified";
      },
      onError: () => {
        setApiError(
          "Something went wrong while saving your details. Please try again.",
        );
      },
    });
    return { requestOk, verified, data };
  };

  // Standalone-modal flow: validate, submit, and toast/close on the same rules
  // EditPanDetailsModal used before this hook existed.
  const handleSave = async ({ onSaveSuccess, onClose } = {}) => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    const { requestOk, verified, data } = await submit();

    if (requestOk) {
      if (onSaveSuccess) onSaveSuccess(data);
      if (verified) {
        toast.success("PAN details saved");
        if (onClose) onClose();
      }
    } else {
      toast.error("Failed to save PAN details");
    }
  };

  const isFormComplete = isEditingExisting
    ? isDirty
    : hasPan === "no"
      ? !!reasonForNoPan &&
        (reasonForNoPan !== OTHER_NO_PAN_REASON || !!otherReasonText.trim()) &&
        !!form60File
      : !!fullName.trim() && !!panNumber.trim() && !!dob && !!panFile;

  return {
    state: {
      hasPan,
      isAadharLinked,
      fullName,
      panNumber,
      dob,
      panFile,
      existingPanDocumentUrl,
      existingPanDocumentName,
      reasonForNoPan,
      otherReasonText,
      isReasonDropdownOpen,
      form60File,
      existingForm60DocumentUrl,
      existingForm60DocumentName,
      errors,
      apiError,
      verificationStatus,
      fileInputRef,
      form60InputRef,
      reasonTriggerRef,
    },
    loading: { isSaving },
    derived: { isFormComplete, isDirty },
    handlers: {
      setHasPan,
      setIsAadharLinked,
      setFullName,
      setPanNumber,
      setDob,
      setReasonForNoPan,
      setOtherReasonText,
      toggleReasonDropdown,
      closeReasonDropdown,
      handleFileSelect,
      handleForm60Select,
      viewDocument,
    },
    mutators: { validate, submit, handleSave },
  };
};
